import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import dig from 'object-dig';
import moment from 'moment';
import { toast } from 'react-toastify';

import PeopleIcon from '@material-ui/icons/People';
import BusinessIcon from '@material-ui/icons/Business';
import SaveAlt from '@material-ui/icons/SaveAlt';

import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Typography from '@material-ui/core/Typography';

import Paper from '~/components/Paper';
import Button from '~/components/Button';
import Dialog from '~/components/Dialog';
import Table from '~/components/Table/Table';
import GridItem from '~/components/GridItem';
import InputSwitch from '~/components/Input/InputSwitch';
import InputDate from '~/components/Input/InputDate';
import GridContainer from '~/components/GridContainer';
import LinearProgress from '~/components/LinearProgress';
import tableStyles from '~/components/Table/Table/styles';
import InputControl from '~/components/Input/InputControl';
import TablePagination from '~/components/Table/TablePagination';
import InputSelectAsync from '~/components/Input/InputSelectAsync';
import InputSearch from '~/components/Input/InputSearch';
import InputSelect from '~/components/Input/InputSelect';
import InputCheckBox from '~/components/Input/InputCheckBox';

import Request from '~/providers/Request';

import toInt from '~/utils/Number/toInt';
import isPresent from '~/utils/isPresent';
import fetchCorporatePartner from '~/utils/fetchCorporatePartner';
import csvGenerator from '~/utils/csvGenerator';
import xlsxGenerator from '~/utils/xlsxGenerator';
import isBlank from '~/utils/isBlank';
import CurrentUser from '~/contexts/CurrentUser';

function AccidentReports() {
  const { t } = useTranslation();
  const classesTable = tableStyles();
  const [loading, setLoading] = useState(false);
  const [userActives, setUserActives] = useState([]);
  const [totalUserActives, setTotalUserActives] = useState(0);
  const [corporation, setCorporation] = useState(null);
  const [dataInit, setDataInit] = useState(moment().startOf('month').format());
  const [dataEnd, setDataEnd] = useState(moment().endOf('month').format());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [inputSearch, setInputSearch] = useState('');
  const [specialty, setSpecialty] = useState({ id: 0, name: t('all') });
  const [specialtiesOptions, setSpecialtiesOptions] = useState([]);
  const [considerBranches, setConsiderBranches] = useState(false);
  const [onlyDependents, setOnlyDependents] = useState(false);
  const [showModalBranches, setShowModalBranches] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [considerAllBranches, setConsiderAllBranches] = useState(true);
  const [branches, setBranches] = useState([]);
  const [fileType, setFileType] = useState('');
  const { user } = useContext(CurrentUser);

  const trAttr = (attr) => t(`accidentReports.attributes.${attr}`);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  async function generateFile(filters, type) {
    const corporation_name = filters.corporation_name;
    delete filters.corporation_name;

    try {
      const registers = await Request.get(
        '/admin/report/medical_appointments_report',
        filters,
        {
          normalizeJSON: false,
        }
      );

      const rows = [];

      let filtersStr = '';
      if(considerBranches) filtersStr += `${t('accidentReports.extras.considerBranches')}, `;
      if(onlyDependents) filtersStr += `${t('only_dependents')}, `;

      rows.push(['Filtros', filtersStr]);
      rows.push(['']);

      rows.push(['Matriz', registers.meta.matriz]);
      rows.push(['Empresa', corporation_name]);
      rows.push(['Período', `${moment(filters.start).format('DD/MM/YYYY HH:mm')} a ${moment(filters.end).format('DD/MM/YYYY HH:mm')}`]);

      rows.push(['']);
      rows.push(['Data Cadastro', 'Paciente', 'Titular', 'Nº Seguro', 'Empresa', 'Especialidade', 'Início Consulta', 'Fim Consulta	']);
      registers.data.map((item) => {
        if (item.specialty_id === null) {
          item.specialty = 'Clínico Geral';
        }
        if(isBlank(item.deleted_at)) rows.push([
          moment(item.created_at).format('DD/MM/YYYY HH:mm'),
          item.name,
          item.owner_name ? item.owner_name : item.name,
          item.policy_id,
          item.user_corporation_name,
          item.specialty,
          moment(item.doctor_init_at).format('DD/MM/YYYY HH:mm'),
          moment(item.doctor_end_at).format('DD/MM/YYYY HH:mm'),
        ]);
        return item;
      });

      rows.push(['']);
      rows.push(['Relatório gerado por', user.name]);
      rows.push(['Relatório gerado em', `${moment().format('L')} - ${moment().format('LT')}`]);

      if (type === 'csv') csvGenerator(`${corporation_name}`, [...rows]);
      else xlsxGenerator(`${corporation_name}.xlsx`, [...rows]);

      toast.success(`Arquivo ${type.toLocaleUpperCase()} de clientes gerado com sucesso!`);

      const totalPages =
        toInt(registers.meta.total) / registers.meta.perPage;
      if (registers.meta.page < totalPages) {
        filters.page += 1;
        filters.corporation_name = corporation_name;
        generateFile(filters, type);
      }
    } catch (error) {
      console.log(error);
      toast.error(
        `Não foi possível gerar o arquivo ${type.toLocaleUpperCase()} dos clientes`
      );
    }
  }


  function onExport(type) {
    const filters = {
      start: dataInit,
      end: dataEnd,
      qs: inputSearch,
      page: 1,
      per_page: 50000,
      corporate_partner_id: dig(corporation, 'id'),
      corporation_id: dig(corporation, 'corporation_id'),
      corporation_name: dig(corporation, 'name'),
      specialty_id: dig(specialty, 'id'),
      consider_branches: false,
      only_dependents: onlyDependents,
    };
    if (considerBranches) {
      setShowModalBranches(true);
    } else {
      toast.info(`Arquivo ${type.toLocaleUpperCase()} esta sendo gerado!`);
      generateFile(filters, type);
    }
  }

  function onExportWithBranches(item, type) {
    const filters = {
      start: dataInit,
      end: dataEnd,
      qs: '',
      page: 1,
      per_page: 50000,
      corporate_partner_id: dig(item, 'id'),
      corporation_id: dig(item, 'corporation_id'),
      corporation_name: dig(item, 'name'),
      specialty_id: dig(specialty, 'id'),
      consider_branches: false,
      only_dependents: onlyDependents,
    };

    generateFile(filters, type);
  }

  async function getUserActives() {
    setLoading(true);

    const filters = {
      start: dataInit,
      end: dataEnd,
      page: page + 1,
      qs: inputSearch,
      per_page: rowsPerPage,
      specialty_id: dig(specialty, 'id'),
      corporate_partner_id: dig(corporation, 'id'),
      corporation_id: dig(corporation, 'corporation_id'),
      only_dependents: onlyDependents,
      consider_branches: considerBranches,
    };

    Request.get('/admin/report/medical_appointments_report', filters, {
      normalizeJSON: false,
    })
      .then((response) => {
        const { data, meta } = response;
        setLoading(false);
        setUserActives(data);
        setPage(toInt(meta.page) - 1);
        setTotalUserActives(toInt(meta.total));
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }

  async function getFirstCorporation() {
    fetchCorporatePartner(null, 0, (data) => {
      setCorporation(data.items[0]);
    });
  }

  async function fetchParams() {
    const url = new URLSearchParams(window.location.search);

    const corporation_id = url.get('corporation_id');
    const start = url.get('start');
    const end = url.get('end');

    if (isPresent(start)) {
      const startDate = moment(start);

      if (startDate.isValid()) {
        setDataInit(startDate.format());
      }
    }
    if (isPresent(end)) {
      const endDate = moment(end);

      if (endDate.isValid()) {
        setDataEnd(endDate.format());
      }
    }

    if (isPresent(corporation_id)) {
      try {
        const { data } = await Request.get(
          `/admin/partner/${corporation_id}`,
          null,
          {
            normalizeJSON: true,
          }
        );

        setCorporation(data);
      } catch (error) {
        console.log(error);
        toast.info(
          'Não foi possível encontrar a empresa informada por parâmetro'
        );
      }
    } else {
      getFirstCorporation();
    }
  }

  async function fetchSpecialties(corporation = null) {
    const obj = corporation ? { corporation_id: corporation.corporation_id } : {};
    Request.get('/admin/specialties', obj, { normalizeJSON: true })
      .then((response) => {
        if(response.data && response.data.length > 0) setSpecialtiesOptions([...response.data, { id: 0, name: t('all') }, {id:99, name: ('Clínico Geral')}]);
        else setSpecialtiesOptions([...response.data, { id: 0, name: t('all') }, {id: 99, name: ('Clínico Geral')}]);
      })
      .catch((e) => {
        console.log(e);
        setSpecialtiesOptions([]);
      });
  }

  useEffect(() => {
    fetchSpecialties(corporation);
  }, [corporation]);

  useEffect(() => {
    if (corporation && dataInit && dataEnd && specialty) {
      getUserActives();
    }
  }, [
    page,
    inputSearch,
    rowsPerPage,
    dataInit,
    dataEnd,
    corporation,
    specialty,
    considerBranches,
    onlyDependents
  ]);

  useEffect(() => {
    fetchParams();
  }, []);

  useEffect(() => {
    async function getBranches() {
      setLoadingBranches(true);

      Request.get(
        `/admin/corporation/${corporation.corporation_id}/branches`,
        null,
        {
          normalizeJSON: true,
        }
      )
        .then((response) => {
          const data = response.data.map((item) => {
            return {
              ...item,
              consider: true,
            };
          });
          setBranches(data);
          setLoadingBranches(false);
        })
        .catch((error) => {
          setLoadingBranches(false);
          console.log(error);
        });
    }
    if (showModalBranches && corporation) {
      getBranches();
    }
  }, [showModalBranches]);

  return (
    <>
      <Paper
        Icon={PeopleIcon}
        iconColor="blue"
        title={t('dashboard.attributes.accident_reports')}
        subtitle={t('dashboard.attributes.accident_reports_description')}
      >
        <GridContainer>
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <InputControl
              name={'corporation'}
              label={t('accidentReports.attributes.corporation')}
              value={dig(corporation, 'name')}
              inputComponent={InputSelectAsync}
              onChange={({ corporation }) => {
                setCorporation(corporation);
              }}
              InputProps={{
                isClearable: false,
                handleFetch: fetchCorporatePartner,
                createEnabled: false,
                error: false,
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataInit}
              name="date"
              label={t('accidentReports.extras.date_init')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) => {
                setDataInit(moment(date).startOf('day').format());
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataEnd}
              name="date"
              label={t('accidentReports.extras.date_end')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) =>
                setDataEnd(moment(date).endOf('day').format())
              }
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={4} md={4} lg={8} xl={4}>
            <InputSearch
              value={inputSearch}
              label={t('action.search')}
              placeholder={t('accidentReports.extras.search')}
              onChange={setInputSearch}
            />
          </GridItem>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>

            <InputControl
              name={'specialty'}
              value={specialty}
              label={t('accidentReports.extras.specialtie')}
              inputComponent={InputSelect}
              onChange={({ specialty }) => {
                setSpecialty(specialty);
              }}
              InputProps={{
                isClearable: false,
                options: specialtiesOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
            <InputSwitch
              label={t('accidentReports.extras.considerBranches')}
              value={considerBranches}
              onChange={setConsiderBranches}
            />
            <InputSwitch
              label={t('only_dependents')}
              value={onlyDependents}
              onChange={setOnlyDependents}
            />
          </GridItem>
          <GridItem align="right">
            <Button
              color="second"
              onClick={() => {
                setFileType('csv');
                onExport('csv');
              }}
            >
              {t('action.export_csv')} <SaveAlt />
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              color="orange"
              onClick={() => {
                setFileType('xlsx');
                onExport('xlsx');
              }}
            >
              {t('action.export_xlsx')} <SaveAlt />
            </Button>
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />

        <Table
          emptyData={userActives.length === 0}
          loading={loading}
          columns={
            <>
              <TableCell className={classesTable.cell}>
                {trAttr('created_At')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('pacient')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('owner')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('policy_number')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('user_corporation_name')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('specialty')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('doctor_init_at')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('doctor_end_at')}
              </TableCell>
            </>
          }
        >
          {userActives.map((row) => {
            if (row.specialty_id === null) {
              row.specialty = 'Clínico Geral';
            } else {
              row.attendant_end_at = null;
            }

            return (
              <TableRow key={`${row.id}-${row.corporation_name}`} hover className={classesTable.row}>
                <TableCell>{moment(row.created_at).format('DD/MM/YYYY HH:mm:ss')}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.owner_name ? row.owner_name : row.name}</TableCell>
                <TableCell>{row.policy_id}</TableCell>
                <TableCell>{row.user_corporation_name}</TableCell>
                <TableCell>{row.specialty}</TableCell>
                <TableCell>{moment(row.doctor_init_at).format('DD/MM/YYYY HH:mm:ss')}</TableCell>
                <TableCell>{moment(row.doctor_end_at).format('DD/MM/YYYY HH:mm:ss')}</TableCell>
              </TableRow>
            );
          })}
        </Table>

        <LinearProgress loading={loading} />

        <Typography
          variant="subtitle2"
          color="primary"
          align="right"
          style={{ paddingRight: 16, marginTop: 10 }}
        >
          <strong>Total: </strong>
          {totalUserActives}
        </Typography>

        <TablePagination
          count={totalUserActives}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
        <Dialog
          icon={<BusinessIcon />}
          isOpen={showModalBranches}
          handleClose={() => setShowModalBranches(false)}
          title={t('accidentReports.extras.considerBranches')}
          dialogActions={
            <Button
              onClick={() => {
                toast.info(
                  `Arquivo ${fileType.toLocaleUpperCase()} esta sendo gerado!`
                );
                onExportWithBranches(corporation, fileType);
                branches.map((item) => {
                  if (item.consider === true) {
                    onExportWithBranches(item, fileType);
                  }
                });
                setShowModalBranches(false);
              }}
              disabled={loadingBranches}
              color="primary"
            >
              {t('action.export')} <SaveAlt />
            </Button>
          }
        >
          <div style={{ minWidth: 560 }}>
            <LinearProgress loading={loadingBranches} />
            <GridContainer>
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <InputSwitch
                  label={t('packageActives.extras.considerAllBranches')}
                  value={considerAllBranches}
                  onChange={(value) => {
                    setBranches(
                      branches.map((item) => {
                        return {
                          ...item,
                          consider: value,
                        };
                      })
                    );
                    setConsiderAllBranches(value);
                  }}
                />
                <Table
                  emptyData={branches.length === 0}
                  loading={loadingBranches}
                  columns={
                    <>
                      <TableCell className={classesTable.cell}>
                        {t('packageActives.extras.consider')}
                      </TableCell>
                      <TableCell className={classesTable.cell}>
                        {trAttr('corporation')}
                      </TableCell>
                    </>
                  }
                >
                  {branches.map((row, index) => {
                    return (
                      <TableRow key={row.id} hover className={classesTable.row}>
                        <TableCell>
                          <InputControl
                            value={row.consider}
                            name="consider"
                            disabled={loading}
                            inputComponent={InputCheckBox}
                            onChange={({ consider }) => {
                              const newBranches = [...branches];
                              newBranches[index].consider = consider;

                              setBranches(newBranches);
                            }}
                          />
                        </TableCell>
                        <TableCell>{row.name}</TableCell>
                      </TableRow>
                    );
                  })}
                </Table>
              </GridItem>
            </GridContainer>
            <LinearProgress loading={loadingBranches} />
          </div>
        </Dialog>
      </Paper>
    </>
  );
}

export default AccidentReports;
